/**
 * useCorrectionCap — load the per-envelope correction cap from
 * `/v3/config/signing-rules` (unseeded in v3.0 → 503 → cap returns `undefined`)
 * AND count existing corrections for the scoped signer role.
 *
 * Legacy: the SPFx `CorrectionRequestModal.tsx:232` hard-coded
 * `MAX_CORRECTIONS_PER_SIGNER = 3`. v3 moves the limit to config so the
 * Supervisor can tune it without a redeploy.
 *
 * If the config route 404s (not yet in the Function) or 503s (seeded table
 * missing) the hook returns `cap = undefined`. Callers treat that as
 * "no enforcement yet" — the banner still renders "count/—" for visibility.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ApiClient } from '@retail-ops/api-client';
import type { CorrectionService } from '../services/CorrectionService';

/** Shape the Function is expected to return from /v3/config/signing-rules. */
interface SigningRulesConfig {
  maxCorrectionsPerEnvelope?: number;
  // Future tuning knobs (not surfaced yet):
  //   allowFieldCorrectionAfterSign?: boolean;
  //   autoCreateSupervisorTaskOnCap?: boolean;
}

interface ApiResultLike<T> {
  data?: T;
  Data?: T;
  success?: boolean;
  rules?: T;
}

export interface UseCorrectionCapOpts {
  sessionId: string | undefined;
  role: string;
  correctionService: CorrectionService;
  apiClient: ApiClient;
}

export interface UseCorrectionCapState {
  cap: number | undefined;
  count: number;
  loading: boolean;
  /** Imperatively re-fetch both config + count (used after a successful submit). */
  refresh: () => void;
}

export function useCorrectionCap(opts: UseCorrectionCapOpts): UseCorrectionCapState {
  const { sessionId, role, correctionService, apiClient } = opts;
  const [cap, setCap] = useState<number | undefined>(undefined);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [bump, setBump] = useState<number>(0);

  const refresh = useCallback((): void => setBump((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    const loadCap = async (): Promise<number | undefined> => {
      try {
        const resp = await apiClient.http.get<ApiResultLike<SigningRulesConfig>>(
          '/v3/config/signing-rules',
        );
        const rules = resp.data ?? resp.Data ?? resp.rules ?? (resp as unknown as SigningRulesConfig);
        const raw = (rules as SigningRulesConfig | undefined)?.maxCorrectionsPerEnvelope;
        return typeof raw === 'number' && raw > 0 ? raw : undefined;
      } catch {
        // 404/503 — unseeded. Return undefined so the banner shows "—".
        return undefined;
      }
    };

    const loadCount = async (): Promise<number> => {
      if (!sessionId) return 0;
      return correctionService.countAttemptsForRole({ sessionId, role });
    };

    setLoading(true);
    void Promise.all([loadCap(), loadCount()]).then(([nextCap, nextCount]) => {
      if (cancelled) return;
      setCap(nextCap);
      setCount(nextCount);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [apiClient, sessionId, role, correctionService, bump]);

  return { cap, count, loading, refresh };
}
